// The Design workspace's centre: the record's forms as tabs (nested forms in
// a strip under their tab), one toolbar (Form / Grid / Feed, device width,
// Try it, the form ▾ menu), the form card with its buttons and fields, and
// two lists of what is set up — on this form, and on the whole record. Every
// item in a menu or a list opens in the inspector through the same TreeNode
// the old tree used.

import { useEffect, useMemo, useRef, useState } from 'react'
import { ChevronDown, Plus, Play, Monitor, Tablet, Smartphone, ArrowRight } from 'lucide-react'
import type { TransactionModel, TreeNode, PreviewMode, DevicePreview, ActionDef } from './types'
import PreviewPanel, { deviceWidths, formModeOf } from './PreviewPanel'
import LineEntryPanel from './LineEntryPanel'
import { allSqlModels, headerFormNo } from '@twasta/page-runtime/txn/utils'
import { appendColumn, makeChildForm } from './formFactory'
import {
  recordTabs, nestedChain, topTabOf, formCategories, recordCategories, formTitle, formButtons, mainTableOf,
  type SetupCategory,
} from './setupCatalog'

interface Props {
  model: TransactionModel
  selectedNode: TreeNode | null
  onSelect: (node: TreeNode) => void
  onModelChange: (model: TransactionModel) => void
  /** The form currently on the canvas (index into sql_models). */
  formIndex: number
  onFormIndex: (fi: number) => void
  project: string
  objectTitle: string
  canEdit: boolean
  onTryIt: () => void
  onOpenLogic: () => void
  onOpenSettings: (node?: TreeNode) => void
  /** Phone layout: tighter paddings, 44px targets. */
  compact?: boolean
}

const PANEL_BG = 'var(--color-bg-secondary)'

function MenuButton({ label, count, active, open, onToggle, title }: {
  label: string; count: string; active: boolean; open: boolean; onToggle: () => void; title: string
}) {
  return (
    <button type="button" onClick={onToggle} aria-haspopup="menu" aria-expanded={open} title={title}
      style={{
        height: 32, display: 'inline-flex', alignItems: 'center', gap: 6, font: 'inherit', fontSize: '0.9375rem',
        borderRadius: 6, padding: '0 10px', cursor: 'pointer', background: PANEL_BG, color: 'var(--color-text)',
        border: `1px solid ${active || open ? 'var(--color-accent)' : 'var(--color-border)'}`,
        boxShadow: active || open ? 'inset 0 0 0 1px var(--color-accent)' : 'none', whiteSpace: 'nowrap',
      }}>
      {label} <span style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>{count}</span>
      <ChevronDown size={12} />
    </button>
  )
}

function MenuList({ sections, onPick, align = 'right' }: {
  sections: Array<{ title?: string; items: Array<{ label: string; n: string; node: TreeNode; settings?: boolean }> }>
  onPick: (node: TreeNode, settings?: boolean) => void
  align?: 'left' | 'right'
}) {
  return (
    <div role="menu" style={{
      position: 'absolute', top: 'calc(100% + 4px)', [align]: 0, zIndex: 30, width: 280,
      background: PANEL_BG, border: '1px solid var(--color-border)', borderRadius: 8,
      boxShadow: '0 8px 24px rgba(0,0,0,0.14)', padding: 6,
    }}>
      {sections.map((sec, si) => (
        <div key={si} style={si ? { borderTop: '1px solid var(--color-border)', marginTop: 4, paddingTop: 4 } : undefined}>
          {sec.title && <div style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--color-text-secondary)', padding: '6px 10px 4px', letterSpacing: '0.02em' }}>{sec.title}</div>}
          {sec.items.map((m, i) => (
            <button key={i} role="menuitem" type="button" onClick={() => onPick(m.node, m.settings)} style={{
              font: 'inherit', width: '100%', textAlign: 'left', border: 'none', background: 'transparent',
              cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 16, padding: '7px 10px',
              color: 'var(--color-text)', fontSize: '0.9375rem', borderRadius: 4,
            }}
              onMouseEnter={e => (e.currentTarget.style.background = 'var(--color-surface-2)')}
              onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
              <span style={{ flex: 1 }}>{m.label}</span>
              <span style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)', whiteSpace: 'nowrap' }}>{m.n}</span>
            </button>
          ))}
        </div>
      ))}
    </div>
  )
}

function SetupGrid({ cats, onPick, onAdd }: {
  cats: SetupCategory[]
  onPick: (node: TreeNode) => void
  onAdd: (cat: SetupCategory) => void
}) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: '16px 24px' }}>
      {cats.map(c => (
        <div key={c.key} style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
          <button type="button" onClick={() => onPick(c.node)} style={{
            font: 'inherit', textAlign: 'left', border: 'none', background: 'none', padding: 0, cursor: 'pointer',
            fontSize: '0.875rem', fontWeight: 600, color: 'var(--color-text)', display: 'flex', gap: 6,
          }}>
            {c.title} <span style={{ fontWeight: 400, color: 'var(--color-text-secondary)' }}>{c.count}</span>
          </button>
          {c.items.slice(0, 6).map(i => (
            <button key={i.id} type="button" onClick={() => onPick(i.node)} title={i.detail} style={{
              font: 'inherit', fontSize: '0.9375rem', textAlign: 'left', border: 'none', background: 'none',
              padding: '2px 0', color: 'var(--color-accent)', cursor: 'pointer', overflow: 'hidden',
              textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: i.mono ? 'var(--font-mono)' : 'inherit',
            }}>{i.name}</button>
          ))}
          {c.items.length > 6 && (
            <button type="button" onClick={() => onPick(c.node)} style={{
              font: 'inherit', fontSize: '0.875rem', textAlign: 'left', border: 'none', background: 'none',
              padding: '2px 0', color: 'var(--color-text-secondary)', cursor: 'pointer',
            }}>+{c.items.length - 6} more</button>
          )}
          <button type="button" onClick={() => onAdd(c)} style={{
            font: 'inherit', fontSize: '0.875rem', textAlign: 'left', border: 'none', background: 'none',
            padding: '2px 0 0', color: 'var(--color-text-secondary)', cursor: 'pointer',
          }}>{c.key === 'rsettings' || c.key === 'lineentry' ? 'Open →' : '+ Add'}</button>
        </div>
      ))}
    </div>
  )
}

export default function DesignCanvas({
  model, selectedNode, onSelect, onModelChange, formIndex, onFormIndex, project, objectTitle,
  canEdit, onTryIt, onOpenLogic, onOpenSettings, compact,
}: Props) {
  const [menu, setMenu] = useState<null | 'form' | 'record'>(null)
  const [device, setDevice] = useState<DevicePreview>('computer')
  const menusRef = useRef<HTMLDivElement>(null)
  const scrollRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!menu) return
    const close = (e: MouseEvent) => {
      const t = e.target
      if (t instanceof Element && t.closest('[data-canvas-menu]')) return
      setMenu(null)
    }
    window.addEventListener('mousedown', close)
    return () => window.removeEventListener('mousedown', close)
  }, [menu])

  const entries = allSqlModels(model)
  const fi = Math.min(Math.max(0, formIndex), Math.max(0, entries.length - 1))
  const sm = entries[fi]?.sm
  const tabs = useMemo(() => recordTabs(model), [model])
  const tabFi = topTabOf(model, fi)
  const chain = useMemo(() => nestedChain(model, tabFi), [model, tabFi])
  // A different form (or its line-entry view) starts at the top.
  const viewKey = `${fi}:${selectedNode?.type === 'form-line-entry' && selectedNode.formIndex === fi ? 'lines' : 'layout'}`
  useEffect(() => { scrollRef.current?.scrollTo({ top: 0 }) }, [viewKey])
  const isHeader = entries[fi]?.formNo === headerFormNo(model)
  const mode: PreviewMode = formModeOf(model, fi)
  const linesView = !isHeader && selectedNode?.type === 'form-line-entry' && selectedNode.formIndex === fi
  const fCats = useMemo(() => formCategories(model, fi), [model, fi])
  const rCats = useMemo(() => recordCategories(model), [model])
  const title = formTitle(sm, fi)
  const buttons = useMemo(() => formButtons(model, fi), [model, fi])

  const pick = (node: TreeNode, settings?: boolean) => {
    setMenu(null)
    if (settings) { onOpenSettings(node); return }
    onSelect(node)
  }

  const setMode = (m: PreviewMode) => {
    if (formModeOf(model, fi) === m) return
    const next: TransactionModel = JSON.parse(JSON.stringify(model))
    next.sql_models[fi].sql_model.view_layout = m
    onModelChange(next)
  }

  const addColumn = (group?: string) => {
    const next: TransactionModel = JSON.parse(JSON.stringify(model))
    const ci = appendColumn(next.sql_models[fi].sql_model, group)
    onModelChange(next)
    const c = next.sql_models[fi].sql_model.columns[ci]?.column
    onSelect({ type: 'column', formIndex: fi, columnIndex: ci, label: c?.name || '', tab: 'general' })
  }

  const addButton = (where: 'form' | 'record') => {
    const next: TransactionModel = JSON.parse(JSON.stringify(model))
    const def: ActionDef = { name: 'New button', handler: 'item_change', business_logic: { method: '', description: '' }, validations: [] }
    if (where === 'form') {
      const list = next.sql_models[fi].sql_model.actions || (next.sql_models[fi].sql_model.actions = [])
      def.order = list.length + 1
      list.push(def)
      onModelChange(next)
      onSelect({ type: 'form-actions', formIndex: fi, actionIndex: list.length - 1, label: def.name })
    } else {
      const list = next.actions || (next.actions = [])
      def.order = list.length + 1
      list.push(def)
      onModelChange(next)
      onSelect({ type: 'object-actions', actionIndex: list.length - 1, label: def.name })
    }
  }

  const addChildForm = () => {
    // "+ Child form" beside the tabs adds a direct child of the header (a new
    // tab); deeper forms are added from Structure › Add form ▾.
    const parentNo = headerFormNo(model)
    const next: TransactionModel = JSON.parse(JSON.stringify(model))
    next.sql_models = [...next.sql_models, makeChildForm(model, parentNo)]
    onModelChange(next)
    const nfi = next.sql_models.length - 1
    onFormIndex(nfi)
    onSelect({ type: 'form', formIndex: nfi, label: 'New form' })
  }

  const onSetupAdd = (c: SetupCategory) => {
    if (c.key === 'fbuttons') return addButton('form')
    if (c.key === 'rbuttons') return addButton('record')
    if (c.key === 'rsettings') return onOpenSettings()
    if (c.key === 'approvals') return onOpenSettings(c.node)
    onSelect(c.node)
  }

  const menuActive = (keys: TreeNode['type'][], formScoped: boolean) =>
    !!selectedNode && keys.includes(selectedNode.type) && (!formScoped || selectedNode.formIndex === fi)
  const formMenuTypes: TreeNode['type'][] = ['form-actions', 'form-field-events', 'form-row-events', 'form-validations', 'form-joins', 'form-cross-updates', 'form-line-entry']
  const recordMenuTypes: TreeNode['type'][] = ['object-actions', 'followup-actions', 'record-saving', 'pre-commit', 'transaction-complete', 'cross-updates', 'approvals', 'record-settings']
  const sumCount = (cats: SetupCategory[]) =>
    String(cats.reduce((n, c) => n + (c.key === 'joins' || c.key === 'lineentry' || c.key === 'rsettings' || c.key === 'approvals' ? 0 : c.items.length), 0))

  const recordMenuSections = [
    { items: rCats.filter(c => c.key === 'rbuttons' || c.key === 'followups').map(c => ({ label: c.title, n: c.count, node: c.node })) },
    { title: 'WHEN SAVING', items: [{ label: 'Before / after saving, cross updates', n: rCats.find(c => c.key === 'saving')?.count || '0', node: { type: 'record-saving', label: 'Before / after saving' } as TreeNode }] },
    { title: 'SETTINGS', items: [
      { label: 'Approvals', n: rCats.find(c => c.key === 'approvals')?.count || 'Off', node: { type: 'approvals', label: 'Approvals' } as TreeNode, settings: true },
      { label: 'Work rules, website, offline…', n: rCats.find(c => c.key === 'rsettings')?.count || '', node: { type: 'record-settings', label: 'Record settings' } as TreeNode },
    ] },
  ]

  const tabBtn = (on: boolean): React.CSSProperties => ({
    font: 'inherit', border: 'none', background: 'none', cursor: 'pointer', whiteSpace: 'nowrap',
    padding: compact ? '13px 12px 11px' : '12px 14px 10px', borderBottom: `2px solid ${on ? 'var(--color-accent)' : 'transparent'}`,
    fontWeight: on ? 600 : 400, color: on ? 'var(--color-text)' : 'var(--color-text-secondary)',
  })
  const segBtn = (on: boolean): React.CSSProperties => ({
    font: 'inherit', fontSize: '0.9375rem', border: 'none', borderRadius: 6, padding: compact ? '8px 12px' : '4px 12px',
    cursor: 'pointer', background: on ? PANEL_BG : 'transparent', fontWeight: on ? 500 : 400,
    color: on ? 'var(--color-text)' : 'var(--color-text-secondary)', boxShadow: on ? '0 1px 2px rgba(0,0,0,0.12)' : 'none',
  })

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      {/* Forms as they appear on the record */}
      <div ref={menusRef} style={{
        background: PANEL_BG, borderBottom: '1px solid var(--color-border)', display: 'flex', alignItems: 'flex-end',
        gap: 2, padding: compact ? '0 8px' : '0 20px', flexShrink: 0, overflowX: compact ? 'auto' : 'visible', flexWrap: compact ? 'nowrap' : 'wrap',
      }} aria-label="Forms as they appear on the record">
        {tabs.map(t => (
          <button key={t.fi} role="tab" aria-selected={t.fi === tabFi} style={tabBtn(t.fi === tabFi)}
            onClick={() => { onFormIndex(t.fi); onSelect({ type: 'form', formIndex: t.fi, label: t.title }) }}>
            {t.title}
            {t.nested > 0 && <span style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)', fontWeight: 400 }}> · {t.nested} nested</span>}
          </button>
        ))}
        {canEdit && (
          <button type="button" onClick={addChildForm} title="Add a detail form under the header" style={{
            font: 'inherit', fontSize: '0.9375rem', border: 'none', background: 'none', cursor: 'pointer',
            padding: '12px 10px 10px', color: 'var(--color-accent)', display: 'inline-flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap',
          }}><Plus size={12} /> Child form</button>
        )}
        <div data-canvas-menu style={{ position: 'relative', marginLeft: 'auto', alignSelf: 'center', padding: '6px 0' }}>
          <MenuButton label="Whole record" count={sumCount(rCats)} open={menu === 'record'}
            active={menuActive(recordMenuTypes, false)}
            onToggle={() => setMenu(menu === 'record' ? null : 'record')}
            title={`Settings for the whole ${objectTitle}: record buttons, follow-ups, save steps, approvals and more`} />
          {menu === 'record' && <MenuList sections={recordMenuSections} onPick={pick} />}
        </div>
      </div>

      {chain.length > 1 && (
        <div style={{
          background: PANEL_BG, borderBottom: '1px solid var(--color-border)', display: 'flex', alignItems: 'center',
          gap: 6, padding: compact ? '6px 8px' : '6px 20px', fontSize: '0.9375rem', overflowX: 'auto', flexShrink: 0,
        }}>
          <span style={{ color: 'var(--color-text-secondary)', whiteSpace: 'nowrap' }}>Inside {chain[0].title}:</span>
          {chain.map(c => (
            <button key={c.fi} type="button" onClick={() => { onFormIndex(c.fi); onSelect({ type: 'form', formIndex: c.fi, label: c.title }) }}
              style={{
                font: 'inherit', fontSize: '0.9375rem', border: 'none', borderRadius: 6, padding: '4px 10px', whiteSpace: 'nowrap', cursor: 'pointer',
                background: c.fi === fi ? 'var(--color-accent-weak)' : 'transparent',
                color: c.fi === fi ? 'var(--color-accent)' : 'var(--color-text)', fontWeight: c.fi === fi ? 600 : 400,
              }}>{'› '.repeat(c.depth)}{c.title}</button>
          ))}
        </div>
      )}

      {/* Toolbar */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, padding: compact ? '8px' : '10px 20px', flexShrink: 0 }}>
        {!isHeader && (
          <div role="tablist" aria-label="Form view" style={{ display: 'flex', gap: 2, background: 'var(--color-surface-2)', borderRadius: 8, padding: 2 }}>
            <button role="tab" aria-selected={!linesView} style={segBtn(!linesView)}
              onClick={() => onSelect({ type: 'form', formIndex: fi, label: title })}>Layout</button>
            <button role="tab" aria-selected={linesView} style={segBtn(linesView)}
              onClick={() => onSelect({ type: 'form-line-entry', formIndex: fi, label: 'How lines are filled' })}>How lines are filled</button>
          </div>
        )}
        {!linesView && (
          <div role="group" aria-label="Preview as" style={{ display: 'flex', alignItems: 'center', gap: 2, background: 'var(--color-surface-2)', borderRadius: 8, padding: 2 }}>
            {(['form', 'grid', 'feed'] as PreviewMode[]).map(m => (
              <button key={m} type="button" aria-pressed={mode === m} style={segBtn(mode === m)} onClick={() => setMode(m)}
                title={m === 'feed' ? 'How one record reads inside something else — a step in a process trail, a record behind a diagram node' : undefined}>
                {m === 'form' ? 'Form' : m === 'grid' ? 'Grid' : 'Feed'}
              </button>
            ))}
            {!compact && (
              <>
                <span aria-hidden style={{ width: 1, height: 18, background: 'var(--color-border)', margin: '0 4px' }} />
                {(['computer', 'tablet', 'mobile'] as DevicePreview[]).map(d => {
                  const Icon = d === 'computer' ? Monitor : d === 'tablet' ? Tablet : Smartphone
                  return (
                    <button key={d} type="button" aria-pressed={device === d} onClick={() => setDevice(d)}
                      aria-label={`${d === 'computer' ? 'Desktop' : d === 'tablet' ? 'Tablet' : 'Phone'} width`}
                      title={`${d === 'computer' ? 'Desktop' : d === 'tablet' ? 'Tablet' : 'Phone'} width`}
                      style={{ ...segBtn(device === d), width: 30, padding: '4px 0', display: 'inline-flex', justifyContent: 'center' }}>
                      <Icon size={14} />
                    </button>
                  )
                })}
              </>
            )}
          </div>
        )}
        {!compact && (
          <button type="button" onClick={onTryIt} title="Try it as a user (opens the saved object in Live Preview)" style={{
            height: 30, border: 'none', background: 'transparent', borderRadius: 6, padding: '0 8px', font: 'inherit',
            fontSize: '0.9375rem', color: 'var(--color-accent)', display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer',
          }}><Play size={12} fill="currentColor" /> Try it</button>
        )}
        <span style={{ flex: '1 1 0' }} />
        <div data-canvas-menu style={{ position: 'relative' }}>
          <MenuButton label={`${title} form`} count={sumCount(fCats)} open={menu === 'form'}
            active={menuActive(formMenuTypes, true) || (selectedNode?.type === 'form' && selectedNode.formIndex === fi && selectedNode.tab === 'data')}
            onToggle={() => setMenu(menu === 'form' ? null : 'form')}
            title="Settings for this form: buttons, events, validations, joins, cross updates" />
          {menu === 'form' && (
            <MenuList onPick={pick} sections={[
              { items: fCats.map(c => ({ label: c.key === 'lineentry' && linesView ? 'Line entry · showing' : c.title, n: c.count, node: c.node })) },
              { items: [{ label: 'Form settings (general, access, list)', n: '', node: { type: 'form', formIndex: fi, label: title } }] },
            ]} />
          )}
        </div>
      </div>

      <div ref={scrollRef} style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        {linesView ? (
          <LineEntryPanel
            model={model}
            formIndex={fi}
            parentSm={(() => {
              const formNo = entries[fi]?.formNo
              const parentNo = sm?.parent_form_no && sm.parent_form_no !== formNo ? String(sm.parent_form_no) : headerFormNo(model)
              return entries.find(e => e.formNo === parentNo)?.sm || entries[0]?.sm || null
            })()}
            isHeader={false}
            project={project}
            onModelChange={onModelChange}
            selectedCommand={selectedNode?.actionIndex}
            onSelectCommand={(i) => onSelect({ type: 'form-line-entry', formIndex: fi, actionIndex: i, label: 'How lines are filled' })}
            onSelectNode={onSelect}
          />
        ) : (
          <div style={{ padding: compact ? '0 8px 24px' : '0 20px 24px' }}>
            <div style={{ maxWidth: deviceWidths[device] === '100%' ? 980 : deviceWidths[device], margin: '0 auto' }}>
              <div style={{ background: PANEL_BG, border: '1px solid var(--color-border)', borderRadius: 8, display: 'flex', flexDirection: 'column' }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', padding: '12px 20px', borderBottom: '1px solid var(--color-border)' }}>
                  <button type="button" title="Main table of this form — click for the form's settings"
                    onClick={() => onSelect({ type: 'form', formIndex: fi, label: title })}
                    style={{
                      font: 'inherit', border: 'none', background: 'none', padding: 0, cursor: 'pointer', textAlign: 'left',
                      fontSize: '0.875rem', color: 'var(--color-text-secondary)', fontFamily: 'var(--font-mono)', flex: '1 1 auto',
                    }}>
                    {mainTableOf(sm) || '(no main table)'} · {(sm?.columns || []).length} columns
                  </button>
                  {buttons.map(b => {
                    const on = selectedNode?.type === 'form-actions' && selectedNode.formIndex === fi && selectedNode.actionIndex === b.node.actionIndex
                    return (
                      <button key={b.id} type="button" title={`Form button — ${b.detail}. Click to edit`} onClick={() => onSelect(b.node)}
                        style={{
                          font: 'inherit', fontSize: '0.9375rem', borderRadius: 6, cursor: 'pointer', color: 'var(--color-text)',
                          padding: on ? '4px 11px' : '5px 12px', background: on ? 'var(--color-accent-weak)' : PANEL_BG,
                          border: on ? '2px solid var(--color-accent)' : '1px solid var(--color-border)',
                        }}>{b.name}</button>
                    )
                  })}
                  {canEdit && (
                    <button type="button" aria-label="Add a form button" title="Add a form button" onClick={() => addButton('form')}
                      style={{
                        width: 30, height: 30, boxSizing: 'border-box', border: '1px dashed var(--color-text-faint)', borderRadius: 6,
                        background: 'transparent', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        color: 'var(--color-accent)', cursor: 'pointer',
                      }}><Plus size={12} /></button>
                  )}
                </div>
                {sm?.form_descr && (
                  <div style={{ padding: '8px 20px 0', fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>{sm.form_descr}</div>
                )}

                <PreviewPanel
                  model={model}
                  selectedNode={selectedNode}
                  onSelectNode={onSelect}
                  onModelChange={onModelChange}
                  formIndex={fi}
                  mode={mode}
                  onAddColumn={canEdit ? addColumn : undefined}
                />

                <section aria-label={`Set up on ${title}`} style={{ background: 'var(--color-surface-2)', borderRadius: '0 0 8px 8px', padding: '16px 20px 18px' }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
                    <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>Set up on {title}</h3>
                    <span style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>Click any item to edit it in the panel</span>
                    <button type="button" onClick={onOpenLogic} style={linkStyle}>See it as a timeline <ArrowRight size={13} /></button>
                  </div>
                  <SetupGrid cats={fCats} onPick={onSelect} onAdd={onSetupAdd} />
                </section>
              </div>

              <section aria-label={`${objectTitle} · whole record`} style={{
                background: PANEL_BG, border: '1px solid var(--color-border)', borderRadius: 8, margin: '16px 0 0', padding: '16px 20px 18px',
              }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
                  <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>{objectTitle} · whole record</h3>
                  <span style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>Applies to every form on the record</span>
                  <button type="button" onClick={() => onOpenSettings()} style={linkStyle}>All record settings <ArrowRight size={13} /></button>
                </div>
                <SetupGrid cats={rCats} onPick={(n) => (n.type === 'approvals' || n.type === 'work-rules' || n.type === 'website' || n.type === 'offline' || n.type === 'assistant-authoring' || n.type === 'vector-search' || n.type === 'file-import' || n.type === 'attachments' || n.type === 'additional-attributes') ? onOpenSettings(n) : onSelect(n)} onAdd={onSetupAdd} />
              </section>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

const linkStyle: React.CSSProperties = {
  marginLeft: 'auto', font: 'inherit', fontSize: '0.9375rem', border: 'none', background: 'none', padding: 0,
  color: 'var(--color-accent)', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4,
}
